import type { IFSItem } from 'bz-core-controls/hooks-form/controls/field-select';

import createContext from 'bz-utilities/hooks/context-v2';
import CacheStore from 'bz-utilities/modules/stores/cached-map-store';
import GUID from 'bz-utilities/modules/variables/guid';

import { computed } from 'mobx';
import type { ITransformer } from 'mobx-utils';
import { computedFn, createTransformer } from 'mobx-utils';
import type {
  GroupStockOverrideResponse,
  StockResponse,
  StockWithLocationResponse
} from 'tm3-core/api.dtos';

import EConfigKeys from 'tm3-core/enums/config-enum';
import StockCategory from 'tm3-core/interfaces/stock/enums/category';

import { EFilterMode } from '../../enums/config/value/finance';
import { cache } from '../index';

const StockKeys = [
  'appointmentLength',
  'batchTraceable',
  'category',
  'id',
  'isMultiple',
  'isPrivateCharge',
  'isVideo',
  'locationBased',
  'locationId',
  'onlineGuid',
  'reference',
  'serviceTypeId'
] as const;

type IStockKeys =
  | 'appointmentLength'
  | 'batchTraceable'
  | 'category'
  | 'id'
  | 'isMultiple'
  | 'isPrivateCharge'
  | 'isVideo'
  | 'locationBased'
  | 'locationId'
  | 'onlineGuid'
  | 'reference'
  | 'serviceTypeId'
  | 'barcode';

export const [InventoryStoreCtx, useInventoryStore] =
  createContext<InventoryStore>('InventoryStore');

export interface IChargeControlStock extends Pick<StockResponse, IStockKeys> {
  isPrePaid: boolean;
  name: string;
  description: string;
  pricingSource?: 'profile' | 'override';
  pricingProfileName?: string;
}
export const UNTAGGED_TAG_SENTINEL = 'No_Tags';

const BookingCategories = [StockCategory.classes, StockCategory.consultancy];

interface ByLocationWithGroupPriceArgs {
  groupId: number;
  locationId?: number;
}

class InventoryStore extends CacheStore<StockWithLocationResponse> {
  constructor() {
    super('inventory');
  }
  @computed
  get Current() {
    return this.All.filter((x) => x.inUse);
  }

  @computed
  get Online() {
    return this.All.filter((x) => x.inUse && x.online && !x.locationId);
  }

  @computed
  get AllOnline() {
    return this.All.filter((x) => x.online && !x.locationId);
  }

  public search(
    searchTerm: string = '',
    privateOnlyCharges = false,
    allowedCatergories: StockCategory[] = []
  ): StockWithLocationResponse[] {
    if (!searchTerm.trim()) return []; // Return early if searchTerm is empty or whitespace

    const lowerSearchTerm = searchTerm.toLowerCase();

    return this.Current.filter((stock) => {
      const matchesCategory =
        !allowedCatergories.length || allowedCatergories.includes(stock.category as StockCategory);
      const matchesPrivateCharge = !privateOnlyCharges || !!stock.isPrivateCharge; // Check private charges only if flag is true
      const matchesDescription = (stock.description ?? '').toLowerCase().includes(lowerSearchTerm);

      return matchesCategory && matchesPrivateCharge && matchesDescription; // Combine conditions
    });
  }

  public readonly ByLocationWithPriceFilter = computedFn(
    (locationId: number): StockWithLocationResponse[] => {
      return this.Current.filter(
        (x) => x.locationBased || !x.locationId || x.locationId === locationId
      );
    }
  );

  public readonly ByLocationWithPriceFormat = computedFn(
    (stock: StockWithLocationResponse, locationId: number): IChargeControlStock => {
      return this.dropDownFormat(stock, true, true, locationId);
    }
  );

  public readonly ByLocationWithPrice = computedFn((locationId: number): IChargeControlStock[] => {
    return this.ByLocationWithPriceFilter(locationId).map((x) =>
      this.ByLocationWithPriceFormat(x, locationId)
    );
  });

  public readonly ByLocationWithGroupPriceFilter = computedFn(
    (args: ByLocationWithGroupPriceArgs): StockWithLocationResponse[] => {
      const { locationId } = args;
      return this.Current.filter((x) => !x.locationId || x.locationId === locationId);
    }
  );

  public readonly ByLocationWithGroupPriceFormat = computedFn(
    (stock: StockWithLocationResponse, args: ByLocationWithGroupPriceArgs): IChargeControlStock => {
      const { locationId, groupId } = args;
      return this.dropDownFormat(stock, true, true, locationId, groupId);
    }
  );

  public readonly ByLocationWithGroupPrice = computedFn(
    (args: ByLocationWithGroupPriceArgs): IChargeControlStock[] => {
      return this.ByLocationWithGroupPriceFilter(args).map((stock) => {
        return this.ByLocationWithGroupPriceFormat(stock, args);
      });
    }
  );
  public readonly ByLocationFilter = computedFn(
    (locationId: number): StockWithLocationResponse[] => {
      return this.Current.filter((x) => !x.locationId || x.locationId === locationId);
    }
  );

  public readonly ByLocationFormat = computedFn(
    (x: StockWithLocationResponse, locationId: number): IChargeControlStock => {
      return this.dropDownFormat(x, false, true, locationId);
    }
  );

  public readonly ByLocation = computedFn((locationId: number): IChargeControlStock[] => {
    return this.ByLocationFilter(locationId).map((x) => this.ByLocationFormat(x, locationId));
  });

  public readonly ForAppointmentSelectionFilter = computedFn(
    (args: AppointmentStockSelectionParams): StockWithLocationResponse[] => {
      const { [EConfigKeys.FILTERMODE]: filterMode = EFilterMode.Off } = cache.config;

      const practitioner = args.practitionerId
        ? cache.practitioners.getById(args.practitionerId)
        : undefined;
      const {
        locationId,
        serviceTypeId,
        overrides,
        types,
        excludeMulti,
        skipFilterMode,
        groupId,
        sessionSkillRestrictions,
        sessionAllowedByTags,
        sessionAllowedByStockIds,
        sessionAllowedTags,
        sessionAllowedStockIds
      } = args;

      return this.All.filter((stock: StockWithLocationResponse) => {
        // Working-time tag / stock-item allow-list. Applies to ALL stocks, including
        // entries forced via `overrides`, so a non-compliant current selection is
        // not silently re-listed. Mirrors AppointmentService.GetSessionAllowListWarning.
        if (sessionAllowedByStockIds) {
          if (!sessionAllowedStockIds || !sessionAllowedStockIds.includes(stock.id)) {
            return false;
          }
        }
        if (sessionAllowedByTags) {
          const allowed = sessionAllowedTags ?? [];
          // Empty allow-list = no tag restriction in effect (matches server).
          if (allowed.length > 0) {
            const tags = stock.tags ?? [];
            const allowsUntagged = allowed.includes(UNTAGGED_TAG_SENTINEL);
            if (tags.length === 0) {
              // Untagged charges only pass when the "No tags" sentinel is in the allow-list.
              if (!allowsUntagged) return false;
            } else if (!allowed.some((t) => t !== UNTAGGED_TAG_SENTINEL && tags.includes(t))) {
              // Tagged charges must overlap at least one real tag; the sentinel doesn't count.
              return false;
            }
          }
        }

        // if it's an override it should be included regardless of OTHER filtering.
        if (overrides && overrides.includes(stock.id)) {
          return true;
        }

        // exclude deactivated stock
        if (!stock.inUse) {
          return false;
        }

        // check stock is of the correct service type id
        if (serviceTypeId > 0 && stock.serviceTypeId !== serviceTypeId) {
          return false;
        }

        // check category filter condition
        if (types.length > 0 && !types.includes(stock.category as 'B' | 'C' | 'S' | 'P')) {
          return false;
        }

        // exclude any multi stock if required
        if (excludeMulti && stock.isMultiple) {
          return false;
        }

        // In flex/strict mode, group-linked filtering only applies when the booking
        // actually belongs to a business/group. Private/self-pay patients should
        // still see the normal charge list unless some other restriction removes items.
        if (filterMode !== EFilterMode.Off && !skipFilterMode) {
          if (groupId > 0) {
            if (!stock.groups?.includes(groupId)) {
              return false;
            }
          }
        }

        // if it has a skill restriction on the session
        if (
          !skipFilterMode &&
          sessionSkillRestrictions?.length > 0 &&
          (!stock.specialismId || !sessionSkillRestrictions.includes(stock.specialismId))
        ) {
          return false;
        }

        // if it has a skill restriction on the practitioners
        if (
          !skipFilterMode &&
          stock.specialismId &&
          !GUID.isEmpty(stock.specialismId) &&
          (!practitioner || !practitioner.skills.includes(stock.specialismId))
        ) {
          return false;
        }

        if (!!cache.isClick) {
          // if click no need to check online status
          return !stock.locationId || stock.locationId === locationId;
        }

        // if it's online it'll have no location id, otherwise, compare on location id
        return (!stock.locationId && stock.online) || stock.locationId === locationId;
      });
    }
  );

  public readonly ForAppointmentSelectionFormat = computedFn(
    (
      stock: StockWithLocationResponse,
      args: AppointmentStockSelectionParams
    ): IChargeControlStock => {
      return this.dropDownFormat(stock, args.showPrice, true, args.locationId, args.groupId);
    }
  );

  public readonly ForAppointmentSelection = computedFn(
    (args: AppointmentStockSelectionParams): IChargeControlStock[] => {
      return this.ForAppointmentSelectionFilter(args).map((x) =>
        this.ForAppointmentSelectionFormat(x, args)
      );
    }
  );

  dropDownPrepaidFormat(stock: StockWithLocationResponse): IChargeControlStock {
    let name = stock.description;
    if (stock.appointmentLength != null) {
      name = name + ' - ' + stock.appointmentLength + ' minute(s)';
    }
    if (stock.reference != null) {
      name = stock.reference + ' - ' + name;
    }
    name += ' [PrePaid]';
    return {
      ...stock.bzPick(...StockKeys),
      description: stock.description,
      name,
      reference: stock.reference || '',
      isPrePaid: true
    };
  }

  @computed
  get CurrentBookingStockFormattedForDropdown(): IFSItem[] {
    return this.Current.filter((c) => BookingCategories.includes(c.category)).map((stock) => {
      return {
        text: this.getDescription(stock),
        payload: stock.id
      };
    });
  }

  @computed
  get CurrentFormattedForDropdown(): IFSItem[] {
    return this.Current.map((stock) => {
      return {
        text: this.getDescription(stock),
        payload: stock.id
      };
    });
  }

  @computed
  get vatRegistered(): boolean {
    return cache.config['VAT REGISTERED'] === 'Y';
  }

  dropDownFormat(
    stock: StockWithLocationResponse,
    showPrice: boolean = false,
    showDuration: boolean = false,
    locationId?: number,
    groupId?: number
  ): IChargeControlStock {
    let name = this.getDescription(stock, showPrice, showDuration, locationId, groupId);
    const groupStockOverride = this.getGroupStockOverride(stock, locationId, groupId);
    const isProfile = !!groupStockOverride && this.isPricingProfileOverride(groupStockOverride);
    const pricingSource = isProfile ? 'profile' : 'override';

    return {
      ...stock.bzPick(...StockKeys),
      name,
      description: stock.description,
      reference: stock.reference || '',
      isPrePaid: false,
      pricingSource: groupStockOverride ? pricingSource : undefined,
      pricingProfileName: isProfile ? groupStockOverride?.priceProfileName || undefined : undefined,
      barcode: stock.barcode
    };
  }

  public getDescription(
    stock: StockWithLocationResponse,
    showPrice: boolean = false,
    showDuration: boolean = false,
    locationId?: number,
    groupId?: number
  ) {
    let name;
    let groupStockOverride: GroupStockOverrideResponse | null | undefined = null;
    const stockOverrides = stock.overrides ?? [];

    if (showPrice) {
      const { price, groupStockOverride: resolvedGroupStockOverride } = this.getDisplayPrice(
        stock,
        locationId,
        groupId
      );
      groupStockOverride = resolvedGroupStockOverride;

      const {
        config: { CURRENCYSYMBOL = '£' }
      } = cache;
      name =
        stock.description +
        ' - ' +
        CURRENCYSYMBOL +
        ' ' +
        (price ?? 0).toFixed(2) +
        (this.vatRegistered ? ' (ex VAT)' : '');
    } else {
      name = stock.description;
    }
    if (
      stock.category === StockCategory.consultancy &&
      stock.appointmentLength != null &&
      showDuration
    ) {
      name = name + ' - ' + stock.appointmentLength + ' minute(s)';
    }
    if (stock.reference != null || groupStockOverride?.reference != null) {
      name = (groupStockOverride?.reference ?? stock.reference) + ' - ' + name;
    }
    if (stock.category === StockCategory.stock) {
      if (
        stock.level === 0 ||
        (locationId &&
          stockOverrides.some((o) => o.locationId === stock.locationId && o.stockLevel === 0))
      ) {
        name += ' [OUT OF STOCK]';
      }
    }
    return name;
  }

  public getDisplayPrice(
    stock: StockWithLocationResponse,
    locationId?: number,
    groupId?: number
  ): { price: number; groupStockOverride?: GroupStockOverrideResponse } {
    let price = this.vatRegistered ? stock.priceExVAT : stock.totalPrice;
    let groupStockOverride: GroupStockOverrideResponse | undefined;
    const stockOverrides = stock.overrides ?? [];

    if (locationId && !(stock.category === StockCategory.package && stock.online)) {
      const stockOverride = stockOverrides.find((x) => x.locationId === locationId);
      if (stockOverride) {
        price = this.vatRegistered ? stockOverride.priceExVAT : stockOverride.totalPrice;
      }

      if (groupId) {
        groupStockOverride = this.getGroupStockOverride(stock, locationId, groupId);
        if (groupStockOverride) {
          price = this.vatRegistered
            ? groupStockOverride.priceExVAT
            : groupStockOverride.totalPrice;
        }
      }
    }

    return {
      price: price ?? 0,
      groupStockOverride
    };
  }

  public readonly getByGuid = createTransformer<string, StockWithLocationResponse | null>((id) => {
    if (!id) {
      return null;
    }
    return this.All.find((item) => item.onlineGuid === id) || null;
  });

  public getGroupStockOverride(
    stock: StockWithLocationResponse | null | undefined,
    locationId?: number,
    groupId?: number
  ): GroupStockOverrideResponse | undefined {
    if (!stock || !groupId) {
      return undefined;
    }
    const groupOverrides = (stock.groupOverrides ?? []).filter((x) => x.groupId === groupId);
    if (!groupOverrides.length) {
      return undefined;
    }

    const exactLocationDirectOverride = locationId
      ? groupOverrides.find((x) => x.locationId === locationId && !x.isFromPricingProfile)
      : undefined;
    if (exactLocationDirectOverride) {
      return exactLocationDirectOverride;
    }

    const exactLocationProfileOverride = locationId
      ? groupOverrides.find((x) => x.locationId === locationId && x.isFromPricingProfile)
      : undefined;
    if (exactLocationProfileOverride) {
      return exactLocationProfileOverride;
    }

    const globalDirectOverride = groupOverrides.find(
      (x) => !x.locationId && !x.isFromPricingProfile
    );
    if (globalDirectOverride) {
      return globalDirectOverride;
    }

    const globalProfileOverride = groupOverrides.find(
      (x) => !x.locationId && x.isFromPricingProfile
    );
    if (globalProfileOverride) {
      return globalProfileOverride;
    }

    return groupOverrides[0];
  }

  private isPricingProfileOverride(groupStockOverride: GroupStockOverrideResponse): boolean {
    const dynamicOverride = groupStockOverride as GroupStockOverrideResponse & {
      pricingProfileId?: number;
      pricingProfileName?: string;
    };

    return (
      !!groupStockOverride.isFromPricingProfile ||
      !!groupStockOverride.priceProfileId ||
      !!groupStockOverride.priceProfileName ||
      !!dynamicOverride.pricingProfileId ||
      !!dynamicOverride.pricingProfileName
    );
  }
}

export class AppointmentStockSelectionParams {
  excludeMulti: boolean = false;
  groupId: number = -1;
  locationId: number = -1;
  overrides?: number[] = [];
  practitionerId: number = -1;
  sessionSkillRestrictions: string[] = [];
  // Working-time allow-list inherited from the session the appointment sits in.
  // Mirrors the AppointmentStore observables of the same names so callers can pass
  // them through verbatim. Empty/false on all four = no allow-list (default).
  // When active, stocks must satisfy the allow-list to be returned -- this includes
  // entries forced in via `overrides`, matching the server-side enforcement.
  sessionAllowedByTags: boolean = false;
  sessionAllowedByStockIds: boolean = false;
  sessionAllowedTags?: string[];
  sessionAllowedStockIds?: number[];
  serviceTypeId: number = 1;
  showPrice: boolean = false;
  skipFilterMode: boolean = false;
  types: ('B' | 'C' | 'S' | 'P')[] = ['C', 'S'];

  public constructor(init?: Partial<AppointmentStockSelectionParams>) {
    // c# object initialiser  style nearly....
    (Object as any).assign(this, init);
  }
}

export default InventoryStore;
